import React, { useEffect, useState, useCallback } from "react";
import { supabase } from "../../../lib/supabaseClient";
import WindSwellRose from "./WindSwellRose";

// Feste Verdrahtung:
// Diese Rosette arbeitet ausschließlich mit attribute_id = 102, Sprache "und"
type Dir = "N" | "NO" | "O" | "SO" | "S" | "SW" | "W" | "NW";
type BoolMap = Record<Dir, boolean>;

type WindProfile = {
  ai?: boolean;
  wind_relevant: boolean;
  wind: BoolMap;
  swell: BoolMap;
};

type ProposalItem = {
  table_name: "location_values";
  row_id: string;
  field_path: "value_json";
  old_value: WindProfile;
  new_value: WindProfile;
  value_type: "json";
};

type Props = {
  locationId: number;
  canEdit?: boolean;
  isLoggedIn?: boolean;
  proposalMode?: boolean;
  onProposalItemChange?: (item: ProposalItem | null) => void;
};

const WIND_PROFILE_LANG = "und" as const;
const WIND_PROFILE_ATTR_ID = 102 as const;

const EMPTY: WindProfile = {
  wind_relevant: false,
  wind: {
    N: false,
    NO: false,
    O: false,
    SO: false,
    S: false,
    SW: false,
    W: false,
    NW: false,
  },
  swell: {
    N: false,
    NO: false,
    O: false,
    SO: false,
    S: false,
    SW: false,
    W: false,
    NW: false,
  },
};

const valuesEqual = (a: WindProfile, b: WindProfile) =>
  JSON.stringify(a) === JSON.stringify(b);

export default function WindProfilePanel({
  locationId,
  canEdit = false,
  isLoggedIn = false,
  proposalMode = false,
  onProposalItemChange,
}: Props) {
  const [value, setValue] = useState<WindProfile>(EMPTY);
  const [originalValue, setOriginalValue] = useState<WindProfile>(EMPTY);
  const [locationValueId, setLocationValueId] = useState<number | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const userCanInteract = canEdit || proposalMode;

  useEffect(() => {
    let mounted = true;

    (async () => {
      setLoading(true);
      setError(null);

      const { data, error: lvErr } = await supabase
        .from("location_values")
        .select("id,value_json")
        .eq("location_id", locationId)
        .eq("attribute_id", WIND_PROFILE_ATTR_ID)
        .eq("language_code", WIND_PROFILE_LANG)
        .maybeSingle();

      if (!mounted) return;

      if (lvErr) {
        console.error("[WindProfilePanel] load error:", lvErr.message);
        setError(lvErr.message);
        setLoading(false);
        return;
      }

      const loadedValue = data?.value_json
        ? (data.value_json as WindProfile)
        : EMPTY;

      setLocationValueId(data?.id ?? null);
      setValue(loadedValue);
      setOriginalValue(loadedValue);
      onProposalItemChange?.(null);

      setLoading(false);
    })();

    return () => {
      mounted = false;
    };
        // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationId]);

  const emitProposalChange = useCallback(
    (nextValue: WindProfile) => {
      if (!proposalMode || !onProposalItemChange) return;

      if (!locationValueId) {
        setError(
          "Windprofil kann nicht als Vorschlag gespeichert werden: location_values.id fehlt."
        );
        onProposalItemChange(null);
        return;
      }

      if (valuesEqual(originalValue, nextValue)) {
        onProposalItemChange(null);
        return;
      }

      onProposalItemChange({
        table_name: "location_values",
        row_id: String(locationValueId),
        field_path: "value_json",
        old_value: originalValue,
        new_value: nextValue,
        value_type: "json",
      });
    },
    [proposalMode, onProposalItemChange, locationValueId, originalValue]
  );

  const updateValue = useCallback(
    (updater: (current: WindProfile) => WindProfile) => {
      setValue((current) => {
        const nextValue = updater(current);
        emitProposalChange(nextValue);
        return nextValue;
      });
    },
    [emitProposalChange]
  );

  const onRoseChange = useCallback(
    (next: { wind: BoolMap; swell: BoolMap }) => {
      updateValue((current) => ({
        ...current,
        wind: next.wind,
        swell: next.swell,
      }));
    },
    [updateValue]
  );

  const save = useCallback(async () => {
    if (!isLoggedIn) {
      setError("Bitte zuerst einloggen.");
      return;
    }

    if (!canEdit) {
      setError("Nur Admins oder Editoren dürfen das Windprofil direkt speichern.");
      return;
    }

    setSaving(true);
    setError(null);

    const payload: WindProfile = { ...value };

    const { data, error: upErr } = await supabase
      .from("location_values")
      .upsert(
        {
          location_id: locationId,
          attribute_id: WIND_PROFILE_ATTR_ID,
          language_code: WIND_PROFILE_LANG,
          value_json: payload,
        },
        { onConflict: "location_id,attribute_id,language_code" }
      )
      .select("id,value_json")
      .single();

    if (upErr) {
      console.error("[WindProfilePanel] save error:", upErr.message);
      setError(upErr.message);
      setSaving(false);
      return;
    }

    const savedValue = data?.value_json ? (data.value_json as WindProfile) : payload;

    setLocationValueId(data?.id ?? locationValueId);
    setValue(savedValue);
    setOriginalValue(savedValue);
    onProposalItemChange?.(null);
    setSaving(false);
  }, [
    locationId,
    value,
    canEdit,
    isLoggedIn,
    locationValueId,
    onProposalItemChange,
  ]);

  if (loading) return <div>Windprofil wird geladen…</div>;

  return (
    <div className="space-y-3">
      {!isLoggedIn && (
        <div className="text-sm text-gray-500">
          Bitte einloggen, um das Windprofil zu bearbeiten.
        </div>
      )}

      {isLoggedIn && proposalMode && (
        <div className="text-sm text-amber-600">
          Änderungen an Wind &amp; Schwell werden als Vorschlag gespeichert.
        </div>
      )}

      {error && <div className="text-red-600">Fehler: {error}</div>}

      <label className="flex items-center gap-2">
        <input
          type="checkbox"
          checked={value.wind_relevant}
          disabled={!userCanInteract}
          onChange={(e) =>
            updateValue((current) => ({
              ...current,
              wind_relevant: e.target.checked,
            }))
          }
        />
        <span>Windrelevant (102)</span>
        {value.ai === true && (
          <span className="text-xs bg-gray-200 rounded px-2 py-0.5">AI</span>
        )}
      </label>

      <WindSwellRose
        editMode={userCanInteract}
        wind={value.wind}
        swell={value.swell}
        onChange={userCanInteract ? onRoseChange : undefined}
      />

      {canEdit && (
        <div className="flex items-center gap-8">
          <button
            type="button"
            onClick={save}
            disabled={saving}
            className="px-4 py-2 rounded bg-black text-white disabled:opacity-50"
          >
            {saving ? "Speichern…" : "Speichern"}
          </button>

          <button
            type="button"
            onClick={() => updateValue(() => EMPTY)}
            disabled={saving}
            className="text-sm underline disabled:opacity-50"
          >
            Zurücksetzen
          </button>
        </div>
      )}
    </div>
  );
}